import React, { useEffect, useState, useRef } from 'react';
import { Button, Form, Select, InputNumber, message, Checkbox, Modal, Tag, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useQuote } from '../context/QuoteContext';
import { getMaterials, getSurfaceTreatments, getToleranceGrades, calculateQuote, submitCalibration } from '../api';


const { Text } = Typography;

// ================= 高级 3D 预览组件 (带工具栏、剖切、截图等) =================
const DfmViewer = ({ glbUrl }) => {
  const containerRef = useRef(null);
  const [wireframe, setWireframe] = useState(false);
  const [showBox, setShowBox] = useState(false);
  const [clipValue, setClipValue] = useState(100); // 0-100
  const threeRef = useRef({});

  useEffect(() => {
    if (!containerRef.current || !glbUrl) return;
    const timer = setTimeout(() => {
      if (!window.THREE || !window.THREE.GLTFLoader) return;
      const viewer = containerRef.current;
      if (!viewer || viewer._initialized) return;
      viewer._initialized = true;

      const scene = new window.THREE.Scene();
      scene.background = new window.THREE.Color(0x0a1628);

      const camera = new window.THREE.PerspectiveCamera(45, viewer.clientWidth / Math.max(viewer.clientHeight, 1), 0.1, 10000);
      camera.position.set(0, 120, 180);

      // 截图需要开启 preserveDrawingBuffer
      const renderer = new window.THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
      renderer.setSize(viewer.clientWidth, viewer.clientHeight);
      renderer.localClippingEnabled = true; 
      viewer.appendChild(renderer.domElement);

      scene.add(new window.THREE.AmbientLight(0xffffff, 0.8));
      const dirLight = new window.THREE.DirectionalLight(0xffffff, 0.8);
      dirLight.position.set(100, 200, 100);
      scene.add(dirLight);

      // 轨道控制
      let controls = null;
      if (window.THREE.OrbitControls) {
         controls = new window.THREE.OrbitControls(camera, renderer.domElement);
         controls.enableDamping = true;
         threeRef.current.controls = controls;
      }

      const clippingPlane = new window.THREE.Plane(new window.THREE.Vector3(0, 0, -1), 0);
      threeRef.current.clippingPlane = clippingPlane;

      const loader = new window.THREE.GLTFLoader();
      loader.load(glbUrl, (gltf) => {
        const model = gltf.scene;
        
        const box = new window.THREE.Box3().setFromObject(model);
        const center = box.getCenter(new window.THREE.Vector3());
        const size = box.getSize(new window.THREE.Vector3());
        
        model.position.x += (model.position.x - center.x);
        model.position.y += (model.position.y - center.y);
        model.position.z += (model.position.z - center.z);

        const maxDim = Math.max(size.x, size.y, size.z);
        const scale = 100 / maxDim;
        model.scale.set(scale, scale, scale);
        
        if (!controls) {
            camera.position.set(0, 100, 150);
            camera.lookAt(0, 0, 0);
        }

        scene.add(model);
        threeRef.current.model = model;

        const boxHelper = new window.THREE.BoxHelper(model, 0x00e5ff);
        boxHelper.visible = false;
        scene.add(boxHelper);
        threeRef.current.boxHelper = boxHelper;
        
        threeRef.current.minZ = box.min.z * scale;
        threeRef.current.maxZ = box.max.z * scale;

        model.traverse((child) => {
          if (child.isMesh) {
            child.material.clippingPlanes = [clippingPlane];
            child.material.clipShadows = true;
            child.material.side = window.THREE.DoubleSide; // 剖切后能看到内部
          }
        });
      });

      const animate = () => {
        requestAnimationFrame(animate);
        if (controls) controls.update();
        renderer.render(scene, camera);
      };
      animate();

      threeRef.current.scene = scene;
      threeRef.current.camera = camera;
      threeRef.current.renderer = renderer;

      const onResize = () => {
        if (!viewer) return;
        camera.aspect = viewer.clientWidth / Math.max(viewer.clientHeight, 1);
        camera.updateProjectionMatrix();
        renderer.setSize(viewer.clientWidth, viewer.clientHeight);
      };
      window.addEventListener('resize', onResize);

      return () => {
        window.removeEventListener('resize', onResize);
        renderer.dispose();
      };
    }, 100);
    return () => clearTimeout(timer);
  }, [glbUrl]);

  useEffect(() => {
    if (threeRef.current.model) {
      threeRef.current.model.traverse((child) => {
        if (child.isMesh && child.material) child.material.wireframe = wireframe;
      });
    }
  }, [wireframe]);

  useEffect(() => {
    if (threeRef.current.boxHelper) threeRef.current.boxHelper.visible = showBox;
  }, [showBox]);

  useEffect(() => {
    if (threeRef.current.clippingPlane && threeRef.current.maxZ !== undefined) {
      const { minZ, maxZ } = threeRef.current;
      const constant = minZ + (maxZ - minZ) * (clipValue / 100);
      threeRef.current.clippingPlane.constant = constant;
    }
  }, [clipValue]);

  const handleScreenshot = () => {
    if (threeRef.current.renderer) {
      const url = threeRef.current.renderer.domElement.toDataURL("image/png");
      const a = document.createElement('a');
      a.href = url;
      a.download = 'DFM-3D.png';
      a.click();
    }
  };

  const resetView = () => {
    if (threeRef.current.camera && threeRef.current.controls) {
      threeRef.current.camera.position.set(0, 120, 180);
      threeRef.current.controls.target.set(0, 0, 0);
      threeRef.current.controls.update();
    }
  };

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
      
      {/* 右侧拉杆 - 剖切 */}
      <div style={{ position: 'absolute', right: 20, top: '50%', transform: 'translateY(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', background: '#fff', padding: '10px 4px', borderRadius: 8, boxShadow: '0 2px 10px rgba(0,0,0,0.2)' }}>
        <span style={{ fontSize: 10, color: '#666', marginBottom: 8 }}>剖切</span>
        <input 
          type="range" min="0" max="100" value={clipValue} 
          onChange={(e) => setClipValue(Number(e.target.value))}
          style={{ writingMode: 'bt-lr', WebkitAppearance: 'slider-vertical', width: 8, height: 150, cursor: 'pointer' }}
        />
      </div>

      {/* 底部工具栏 */}
      <div style={{ position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 15, background: '#fff', padding: '8px 20px', borderRadius: 8, boxShadow: '0 2px 10px rgba(0,0,0,0.2)', alignItems: 'center' }}>
         <button onClick={resetView} title="重置视角" style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 18 }}>🎯</button>
         <button onClick={() => setWireframe(!wireframe)} title="线框模式" style={{ background: wireframe ? '#e0f7fa' : 'transparent', border: 'none', cursor: 'pointer', fontSize: 18, borderRadius: 4 }}>📐</button>
         <button onClick={() => setShowBox(!showBox)} title="包围盒" style={{ background: showBox ? '#e0f7fa' : 'transparent', border: 'none', cursor: 'pointer', fontSize: 18, borderRadius: 4 }}>📦</button>
         <button onClick={handleScreenshot} title="截图下载" style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 18 }}>📷</button>
      </div>
    </div>
  );
};

// ================= 3D 预览组件（直接使用全局引入的 Three.js） =================
const StepPreviewInline = ({ glbUrl }) => {
  const containerRef = useRef(null);
  
  useEffect(() => {
    if (!containerRef.current || !glbUrl) return;
    
    // 延迟 100ms 确保容器有宽高
    const timer = setTimeout(() => {
      if (!window.THREE || !window.THREE.GLTFLoader) return;
      
      const viewer = containerRef.current;
      if (!viewer || viewer._initialized) return;
      viewer._initialized = true;

      const scene = new window.THREE.Scene();
      scene.background = new window.THREE.Color(0x0a1628);
      
      const camera = new window.THREE.PerspectiveCamera(45, viewer.clientWidth / Math.max(viewer.clientHeight, 1), 0.1, 1000);
      camera.position.set(0, 1.5, 2.5);
      
      const renderer = new window.THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.setSize(viewer.clientWidth, viewer.clientHeight);
      viewer.appendChild(renderer.domElement);

      scene.add(new window.THREE.AmbientLight(0xffffff, 0.6));
      const dirLight = new window.THREE.DirectionalLight(0xffffff, 0.8);
      dirLight.position.set(5, 5, 5);
      scene.add(dirLight);

      const loader = new window.THREE.GLTFLoader();
      loader.load(glbUrl, (gltf) => {
        const model = gltf.scene;
        scene.add(model);
        const box = new window.THREE.Box3().setFromObject(model);
        const center = box.getCenter(new window.THREE.Vector3());
        model.position.sub(center);
        camera.lookAt(0, 0, 0);
        renderer.render(scene, camera);
      });
    }, 100);

    return () => clearTimeout(timer);
  }, [glbUrl]);

  if (!glbUrl) return <div style={{ color: '#556', fontSize: 12, display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>无3D预览</div>;
  return <div ref={containerRef} style={{ width: '100%', height: '100%' }} />;
};

export default function Parameters() {
  const { state, dispatch } = useQuote();
  const navigate = useNavigate();
  const parts = state.parts || [];

  const [materials, setMaterials] = useState([]);
  const [treatments, setTreatments] = useState({ groups: [] });
  const [toleranceGrades, setToleranceGrades] = useState([]);
  const [liveResults, setLiveResults] = useState({});
  const [calculating, setCalculating] = useState(false);
  
  const [selectedLeadType, setSelectedLeadType] = useState('standard');
  const [selectedParts, setSelectedParts] = useState([]);
  
  const [editingPartIndex, setEditingPartIndex] = useState(null);
  const [previewPart, setPreviewPart] = useState(null);
  const [actualPrices, setActualPrices] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // 1. 初始化数据并赋默认值
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [matRes, treatRes, tolRes] = await Promise.all([getMaterials(), getSurfaceTreatments(), getToleranceGrades()]);
        setMaterials(matRes.data); setTreatments(treatRes.data); setToleranceGrades(tolRes.data);

        let changed = false;
        const newParts = parts.map(part => {
          const np = { ...part };
          if (!np.quantity) { np.quantity = 1; changed = true; }
          if (!np.roughnessRa) { np.roughnessRa = 3.2; changed = true; }
          if (!np.toleranceGrade) { np.toleranceGrade = '未标注/自由公差'; changed = true; }
          if (!np.material && matRes.data.length > 0) {
            for (let cat of matRes.data) {
              const found = cat.items.find(i => i.name.includes('6061'));
              if (found) { np.material = found; changed = true; break; }
            }
            if (!np.material) { np.material = matRes.data[0]?.items[0]; changed = true; }
          }
          return np;
        });
        if (changed) dispatch({ type: 'SET_PARTS', payload: newParts });
      } catch (err) { message.error('加载数据失败'); }
    };
    if (parts.length === 0) navigate('/upload');
    else fetchData();
  }, []);

  // 2. 实时计算价格
  useEffect(() => {
    if (parts.length === 0 || materials.length === 0) return;
    const timer = setTimeout(async () => {
      setCalculating(true);
      const newResults = {};
      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        if (!part.material || !part.result) continue;
        try {
          const analysisResult = { ...part.result };
          if (part.selectedMachine) analysisResult.recommended_machine = part.selectedMachine;
          const { data } = await calculateQuote({
            task_id: part.taskId, analysis_result: analysisResult, material: part.material,
            quantity: part.quantity, surface_treatments: part.surfaceTreatments || [],
            tolerance_grade: part.toleranceGrade, roughness_ra: part.roughnessRa,
            recommended_machine: part.selectedMachine || analysisResult.recommended_machine || '3-axis mill',
          });
          newResults[i] = data; // 保存完整数据
        } catch (e) { 
          console.error(e); 
          message.error(`零件 ${i+1} 计算失败: ` + (e.response?.data?.detail || e.message));
        }
      }
      setLiveResults(newResults);
      setCalculating(false);
    }, 800);
    return () => clearTimeout(timer);
  }, [parts, materials]);

  const toggleCard = (idx, checked) => {
    if (checked) setSelectedParts(prev => [...prev, idx]);
    else setSelectedParts(prev => prev.filter(i => i !== idx));
  };
  const toggleAll = () => {
    if (selectedParts.length === parts.length) setSelectedParts([]);
    else setSelectedParts(parts.map((_, i) => i));
  };

  const handleCalibrate = async (idx) => {
    const actual = actualPrices[idx];
    if (!actual || actual <= 0) return message.warning('请输入有效成交价');
    setSubmitting(true);
    const part = parts[idx];
    try {
      await submitCalibration({
        task_id: part.taskId || 'unknown', part_name: part.cadFile?.name || `零件 ${idx + 1}`,
        material_category: part.material?.category || '', material_name: part.material?.name || '',
        quantity: part.quantity || 1, volume_cm3: part.result?.volume_cm3 || 0,
        difficulty_coefficient: part.result?.total_difficulty_coefficient || 1.0,
        recommended_machine: part.result?.recommended_machine || '3-axis mill',
        system_price: liveResults[idx]?.breakdown?.total_price || 0, actual_price: actual,
        tolerance_grade: part.toleranceGrade, roughness_ra: part.roughnessRa,
      });
      message.success('校准成功');
      setActualPrices(prev => ({ ...prev, [idx]: null }));
    } catch (e) { message.error('校准失败'); }
    setSubmitting(false);
  };

  const updatePartData = (formValues) => {
    const newParts = [...parts];
    if (formValues.material && formValues.material.value) {
      const matId = formValues.material.value;
      let foundMat = null;
      for (let cat of materials) {
        const m = cat.items.find(i => i.id === matId);
        if (m) { foundMat = m; break; }
      }
      formValues.material = foundMat;
    }
    newParts[editingPartIndex] = { ...newParts[editingPartIndex], ...formValues };
    dispatch({ type: 'SET_PARTS', payload: newParts });
    setEditingPartIndex(null);
  };

  const totalBasePrice = Object.values(liveResults).reduce((a, b) => a + (b?.breakdown?.total_price || 0), 0);
  const leadMultipliers = { economy: 0.8, standard: 1.0, expedited: 1.2 };
  const leadDaysRange = { economy: '8-10', standard: '6-8', expedited: '4-5' };
  const currentTotalPrice = totalBasePrice * leadMultipliers[selectedLeadType];
  const categoryLabel = { aluminum: 'Aluminum', carbon_steel: 'Carbon Steel', stainless_steel: 'Stainless Steel' };

  if (parts.length === 0) return null;

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: '#000814', color: '#e0e0e0', fontFamily: '-apple-system, sans-serif', zIndex: 10000, display: 'flex' }}>
      
      {/* 左侧导航 */}
      <div style={{ width: 210, background: '#000814', borderRight: '1px solid rgba(0,229,255,0.1)', padding: '20px 14px', display: 'flex', flexDirection: 'column' }}>
        <div style={{ fontSize: 18, fontWeight: 900, color: '#00e5ff', letterSpacing: 1, marginBottom: 40 }}>PROLEAN</div>
        <div onClick={() => navigate('/')} style={{ color: '#fff', fontSize: 15, padding: '10px 8px', cursor: 'pointer', borderRadius: 6 }}>
          <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#00e5ff', marginRight: 8 }}></span>New quote
          <span style={{ fontSize: 12, color: '#8899aa', marginLeft: 16, display: 'block' }}>新报价</span>
        </div>
      </div>

      {/* 中间主体 */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        
        <div style={{ flex: 1, overflowY: 'auto', padding: '30px 40px' }}>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div style={{ fontSize: 22, fontWeight: 'bold', color: '#fff' }}>报价结果 <span style={{fontSize: 14, color:'#00e5ff', marginLeft:10, fontWeight:'normal'}}>Quote Results</span></div>
            <div style={{ display: 'flex', gap: 15, alignItems: 'center', fontSize: 13 }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: 8, color: '#8899aa' }}>
                <input type="checkbox" checked={selectedParts.length === parts.length && parts.length > 0} onChange={toggleAll} style={{ accentColor: '#00e5ff', width: 16, height: 16 }} /> 
                全选 Select All
              </label>
              <Button disabled={selectedParts.length === 0} style={{ background: 'transparent', border: '1px solid #00e5ff', color: '#00e5ff', borderRadius: 4, padding: '4px 12px', fontSize: 12 }}>批量设置选中零件 Batch Set</Button>
            </div>
          </div>

          {parts.map((part, idx) => {
            const result = part.result || {};
            const dims = result.dimensions_mm || [0, 0, 0];
            const vol = result.volume_cm3 || 0;
            const qty = part.quantity || 1;
            const basePrice = liveResults[idx]?.breakdown?.total_price || 0;
            const unitPrice = basePrice / qty;
            const isSelected = selectedParts.includes(idx);
            const hasThreads = result.features?.threaded_holes > 0;
            const matName = part.material?.name || 'Aluminum 6061-T6';

            return (
              <div key={idx} style={{ 
                background: '#071526', 
                border: `1px solid ${isSelected ? '#00e5ff' : 'rgba(0, 229, 255, 0.15)'}`, 
                borderRadius: 8, display: 'flex', padding: 24, gap: 30, marginBottom: 20, minWidth: 700
              }}>
                
                {/* 左侧：3D图 */}
                <div style={{ flex: '0 0 200px', display: 'flex', flexDirection: 'column' }}>
                  <div style={{ width: '100%', height: 140, background: '#0a1628', border: '1px solid rgba(0,229,255,0.2)', borderRadius: 4, overflow: 'hidden', marginBottom: 12 }}>
                    <StepPreviewInline glbUrl={result.glb_url} />
                  </div>
                  <button onClick={() => setPreviewPart(part)} style={{ display: 'block', background: 'transparent', color: '#00e5ff', border: '1px solid #00e5ff', padding: '6px 10px', borderRadius: 4, fontSize: 12, textAlign: 'center', cursor: 'pointer', marginBottom: 15 }}>
                    View DFM analysis
                  </button>
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 12, color: '#8899aa', marginBottom: 4 }}>Process Machine 工艺设备:</div>
                    <Select
                      size="small"
                      value={part.selectedMachine || result.recommended_machine || '3-axis mill'}
                      onChange={(val) => {
                        // 兼容不同版本的 state 更新
                        const newParts = [...parts];
                        newParts[idx] = { ...newParts[idx], selectedMachine: val };
                        if (dispatch) {
                          dispatch({ type: 'SET_PARTS', payload: newParts });
                          // 如果上下文没定义 SET_PARTS，强行用更新后的 parts 触发重算
                          setTimeout(() => { setLiveResults({}); }, 10); 
                        }
                      }}
                      style={{ width: '100%' }}
                      popupMatchSelectWidth={false}
                      getPopupContainer={triggerNode => triggerNode.parentNode}
                      options={[
                        { label: '3-axis mill (三轴立加)', value: '3-axis mill' },
                        { label: '4-axis mill (四轴加工中心)', value: '4-axis mill' },
                        { label: '5-axis mill (五轴联动)', value: '5-axis mill' },
                        { label: 'lathe (数控车床)', value: 'lathe' },
                        { label: 'mill-turn (车铣复合)', value: 'mill-turn' },
                        { label: 'cnc_wire_cut (线切割)', value: 'cnc_wire_cut' },
                        { label: 'cnc_grinding (磨床)', value: 'cnc_grinding' },
                      ]}
                    />
                  </div>
                  <div style={{ fontSize: 13, color: '#c0c8d0', lineHeight: 2 }}>
                    <span>{Array.isArray(dims) ? dims.map(d => Number(d || 0).toFixed(1)).join(' × ') : '0.0 × 0.0 × 0.0'} mm</span><br/>
                    <span>{vol.toLocaleString()} mm³</span>
                  </div>
                </div>

                {/* 中间：参数列表 */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                    <input type="checkbox" checked={isSelected} onChange={e => toggleCard(idx, e.target.checked)} style={{ accentColor: '#00e5ff', width: 16, height: 16 }} />
                    <div style={{ fontSize: 16, fontWeight: 600, color: '#fff', borderLeft: '2px solid #00e5ff', paddingLeft: 10, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {part.cadFile?.name || `零件 ${idx + 1}`}
                    </div>
                  </div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr', gap: '8px 10px', fontSize: 13, color: '#a0b0c0' }}>
                    <div style={{ color: '#556' }}>Process 工艺:</div><div>{liveResults[idx]?.recommended_machine || part.selectedMachine || result.recommended_machine || 'CNC machining (3-axis)'}</div>
                    <div style={{ color: '#556' }}>Material 材料:</div><div>{matName}</div>
                    <div style={{ color: '#556' }}>Surface 表面:</div><div>{part.surfaceTreatments?.length > 0 ? part.surfaceTreatments.map(s=>s.name).join(', ') : 'As machined'} (Ra {part.roughnessRa}μm)</div>
                    <div style={{ color: '#556' }}>Markings 标记:</div><div>No part markings</div>
                    <div style={{ color: '#556' }}>Tolerance 公差:</div><div>{part.toleranceGrade}</div>
                    <div style={{ color: '#556' }}>Threads 螺纹:</div><div>{hasThreads ? result.features.thread_details.map(t=>t.designation).join(',') : 'No threads'}</div>
                  </div>

                  <div style={{ color: '#ffcc00', marginTop: 16, background: 'rgba(255,204,0,0.05)', padding: '8px 12px', borderRadius: 4, borderLeft: '2px solid #ffcc00', fontSize: 13, display: 'flex', gap: 10 }}>
                    <strong style={{ whiteSpace: 'nowrap' }}>Factory Rule:</strong> <span>Sharp internal corners rounded to 2mm</span>
                  </div>
                </div>

                {/* 右侧：价格与操作 */}
                <div style={{ flex: '0 0 170px', textAlign: 'right', display: 'flex', flexDirection: 'column' }}>
                  <div style={{ marginBottom: 'auto' }}>
                    <div style={{ fontSize: 13, color: '#8899aa' }}>Qty: {qty}</div>
                    <div style={{ fontSize: 26, fontWeight: 800, color: '#00e5ff', margin: '4px 0', textShadow: '0 0 10px rgba(0,229,255,0.3)' }}>
                      ${basePrice ? Number(basePrice).toFixed(2) : '--'}
                    </div>
                    <div style={{ fontSize: 12, color: '#556' }}>${unitPrice ? Number(unitPrice).toFixed(2) : '--'} p/part</div>
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 20 }}>
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                      <input type="number" placeholder="实际成交价" value={actualPrices[idx] || ''} onChange={(e) => setActualPrices(prev => ({ ...prev, [idx]: e.target.value }))} style={{ width: '100%', background: '#000814', border: '1px solid #556', color: '#fff', padding: '6px 8px', borderRadius: 4, fontSize: 12 }} />
                      <button onClick={() => handleCalibrate(idx)} disabled={submitting} style={{ background: 'transparent', border: '1px solid #556', color: '#8899aa', padding: '0 10px', borderRadius: 4, cursor: 'pointer', fontSize: 12, whiteSpace: 'nowrap' }}>校准</button>
                    </div>
                    <button onClick={() => setEditingPartIndex(idx)} style={{ width: '100%', background: 'transparent', color: '#00e5ff', border: '1px solid #00e5ff', padding: '8px', borderRadius: 4, fontSize: 13, fontWeight: 'bold', cursor: 'pointer' }}>
                      ✎ Change specs
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 右侧交期栏 */}
      <div style={{ width: 320, background: 'rgba(0,10,20,0.95)', borderLeft: '1px solid rgba(0,229,255,0.1)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '30px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#00e5ff', fontSize: 16, fontWeight: 'bold', marginBottom: 10 }}>
            <span>📊 订单总价 Total Price</span>
          </div>
          <div style={{ fontSize: 36, fontWeight: 900, color: '#00e5ff', textShadow: '0 0 15px rgba(0,229,255,0.3)', marginBottom: 5 }}>
            ${calculating ? '...' : Number(currentTotalPrice || 0).toFixed(2)}
          </div>
          <div style={{ fontSize: 13, color: '#8899aa', marginBottom: 30 }}>包含 {parts.length} 个零件</div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#00e5ff', fontSize: 15, fontWeight: 'bold', marginBottom: 15 }}>
            <span>🚚 交期 Lead Time</span>
          </div>
          
          {['economy', 'standard', 'expedited'].map(type => (
            <div key={type} onClick={() => setSelectedLeadType(type)} style={{ 
              display: 'flex', alignItems: 'center', padding: '14px 16px', cursor: 'pointer', marginBottom: 10,
              border: `1px solid ${selectedLeadType === type ? '#00e5ff' : 'transparent'}`,
              background: selectedLeadType === type ? 'rgba(0,229,255,0.05)' : 'transparent',
              borderRadius: 6, transition: 'all 0.2s'
            }}>
              <div style={{ width: 16, height: 16, borderRadius: '50%', border: `4px solid ${selectedLeadType === type ? '#00e5ff' : '#556'}`, background: '#000', marginRight: 12 }}></div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#e0e0e0' }}>
                  {type === 'economy' ? '经济 Economy' : type === 'standard' ? '正常 Standard' : '加急 Expedited'}
                </div>
                <div style={{ fontSize: 12, color: '#8899aa' }}>{leadDaysRange[type]} 天</div>
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#00e5ff', whiteSpace: 'nowrap' }}>${calculating ? '--' : Number((totalBasePrice || 0) * (leadMultipliers[type] || 1)).toFixed(2)}</div>
            </div>
          ))}
        </div>
      </div>

      {/* 参数修改弹窗 (zIndex 提升) */}
      <Modal 
        title={<span style={{ color: '#00e5ff' }}>Change Specifications</span>} 
        open={editingPartIndex !== null} 
        onCancel={() => setEditingPartIndex(null)} 
        footer={null}
        zIndex={10050}
        styles={{ content: { background: '#071526', border: '1px solid #00e5ff' }, header: { background: '#071526', borderBottom: '1px solid rgba(0,229,255,0.2)' } }}
      >
        {editingPartIndex !== null && (
          <Form layout="vertical" onFinish={updatePartData} initialValues={{
            ...parts[editingPartIndex],
            material: parts[editingPartIndex].material ? { value: parts[editingPartIndex].material.id, label: parts[editingPartIndex].material.name } : undefined,
            surfaceTreatments: (parts[editingPartIndex].surfaceTreatments || []).map(t => t.code)
          }}>
            <Form.Item label="材料 Material" name="material" rules={[{ required: true }]}>
              <Select showSearch labelInValue>
                {materials.map(cat => (
                  <Select.OptGroup key={cat.category} label={categoryLabel[cat.category] || cat.display_name}>
                    {cat.items.map(item => <Select.Option key={item.id} value={item.id} label={item.name}>{item.name}</Select.Option>)}
                  </Select.OptGroup>
                ))}
              </Select>
            </Form.Item>
            <Form.Item label="数量 Quantity" name="quantity" rules={[{ required: true }]}><InputNumber min={1} style={{ width: '100%' }} /></Form.Item>
            <Form.Item label="表面处理 Surface" name="surfaceTreatments">
              <Select mode="multiple">
                {treatments.groups?.map(group => (
                  <Select.OptGroup key={group.group_name} label={group.group_name}>
                    {group.items.map(item => <Select.Option key={item.code} value={item.code}>{item.name}</Select.Option>)}
                  </Select.OptGroup>
                ))}
              </Select>
            </Form.Item>
            <Form.Item label="公差 Tolerance" name="toleranceGrade">
              <Select>{toleranceGrades.map(g => <Select.Option key={g.name} value={g.name}>{g.name}</Select.Option>)}</Select>
            </Form.Item>
            <Form.Item label="粗糙度 Ra" name="roughnessRa">
              <Select><Select.Option value={3.2}>Ra 3.2</Select.Option><Select.Option value={1.6}>Ra 1.6</Select.Option><Select.Option value={0.8}>Ra 0.8</Select.Option></Select>
            </Form.Item>
            <Button type="primary" htmlType="submit" block style={{ background: '#00e5ff', color: '#000', fontWeight: 'bold', marginTop: 10 }}>保存参数 Save</Button>
          </Form>
        )}
      </Modal>

      {/* DFM 弹窗 (zIndex 提升) */}
      <Modal 
        title={<span style={{ color: '#00e5ff' }}>DFM Analysis</span>} 
        open={previewPart !== null} 
        onCancel={() => setPreviewPart(null)} 
        footer={null} 
        width="80%"
        zIndex={10050}
        styles={{ content: { background: '#071526', border: '1px solid #00e5ff' }, header: { background: '#071526', borderBottom: '1px solid rgba(0,229,255,0.2)' } }}
      >
                {previewPart && (() => {
          const result = previewPart?.result || previewPart?.part?.result || {};
          const previewPartIndex = parts.findIndex(p => p.taskId === previewPart?.taskId);
          const currentResult = liveResults[previewPartIndex] || result;
          const dfmResults = currentResult.dfm_results || result.dfm_results || [];
          const glbUrl = result.glb_url;

          const iconOf = (s) => s === 'ok' ? '✅' : s === 'warning' ? '⚠️' : '❌';
          const colorOf = (s) => s === 'ok' ? '#00ff88' : s === 'warning' ? '#ffcc00' : '#ff4444';

          return (
            <div style={{ display: 'flex', gap: 20, height: '65vh' }}>
              <div style={{ flex: 1, background: '#0a1628', border: '1px solid rgba(0,229,255,0.2)', borderRadius: 8, overflow: 'hidden', display: 'flex', position: 'relative' }}>
                {glbUrl ? (
                  <DfmViewer glbUrl={glbUrl} />
                ) : (
                  <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8899aa' }}>
                    无 3D 预览
                  </div>
                )}
              </div>

              <div style={{ flex: 1, background: 'rgba(0,20,40,0.85)', border: '1px solid rgba(0,229,255,0.2)', borderRadius: 8, padding: 16, overflow: 'auto' }}>
                <Text strong style={{ color: '#00e5ff', fontSize: 16 }}>DFM 可制造性评估</Text>

                <div style={{ marginTop: 16 }}>
                  {dfmResults.length > 0 ? (
                    dfmResults.map((r, i) => (
                      <div key={i} style={{ fontSize: 13, marginBottom: 8, color: '#e0e0e0', lineHeight: 1.6 }}>
                        {iconOf(r.status)}{' '}
                        <span style={{ color: colorOf(r.status), fontWeight: 600 }}>{r.item}</span>
                        ：{r.detail}
                      </div>
                    ))
                  ) : (
                    <Text type="secondary" style={{ fontSize: 13 }}>暂无 DFM 结果</Text>
                  )}
                </div>
              </div>
            </div>
          );
        })()}
      </Modal>

    </div>
  );
}
